"use client";
import { motion, useInView, useScroll, useTransform } from "framer-motion";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { getMediaUrl } from "@/lib/utils";
import servicesData from "@/data/services.json";

const ServicesSection = ({ data }) => {
  // Fallback to hardcoded values if data is not available
  const title = data?.title || "خدماتنا";
  const subtitle = data?.subtitle || "خدمات نقدمها";
  const description =
    data?.description ||
    "مع كل عميل من عملائنا ، نظهر شغفًا عميقًا للاعلان عن العلامات التجارية بشكل إبداعية";

  const sectionRef = useRef(null);
  const headerRef = useRef(null);
  const [floatingShapes, setFloatingShapes] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDesktop, setIsDesktop] = useState(false);

  // IMPORTANT: Keep the element rendered so the ref hydrates.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const headerY = useTransform(scrollYProgress, [0, 0.3], [100, 0]);
  const isHeaderInView = useInView(headerRef, { once: true });

  useEffect(() => {
    const shapes = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      top: 20 + Math.random() * 60,
      left: 10 + Math.random() * 80,
      delay: Math.random() * 2,
      duration: 4 + Math.random() * 2,
    }));
    setFloatingShapes(shapes);

    const checkIsDesktop = () => {
      setIsDesktop(window.innerWidth >= 1024);
    };

    checkIsDesktop();
    window.addEventListener("resize", checkIsDesktop);

    const fetchFeaturedServices = async () => {
      try {
        console.log("[ServicesSection] Fetching featured services...");
        const { getServices } = await import("@/lib/api");
        const featuredServices = await getServices("ar", true); // true = featured only
        console.log(
          "[ServicesSection] Featured services received:",
          featuredServices
        );
        const filtered = (featuredServices || []).filter((s) => s && typeof s === 'object' && s.id);
        setServices(filtered.slice(0, 4));
      } catch (error) {
        console.error(
          "[ServicesSection] Error fetching featured services:",
          error
        );
        setServices([]);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedServices();

    return () => window.removeEventListener("resize", checkIsDesktop);
  }, []);

  return (
    <section ref={sectionRef} className="py-20 overflow-hidden relative">
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-[#dfa428]/5 to-[#0a367b]/5 rounded-full"
          animate={{
            rotate: [0, 360],
            scale: [1, 1.2, 1],
            x: [0, 50, 0],
          }}
          transition={{ duration: 20, repeat: Number.POSITIVE_INFINITY }}
        />
        <motion.div
          className="absolute bottom-20 left-20 w-48 h-48 bg-gradient-to-tr from-[#0a367b]/5 to-[#dfa428]/5 rounded-full"
          animate={{
            rotate: [360, 0],
            scale: [1.2, 1, 1.2],
            y: [0, -30, 0],
          }}
          transition={{ duration: 15, repeat: Number.POSITIVE_INFINITY }}
        />

        {floatingShapes.map((shape) => (
          <motion.div
            key={shape.id}
            className="absolute w-4 h-4 bg-[#dfa428]/20 rounded-full"
            style={{
              top: `${shape.top}%`,
              left: `${shape.left}%`,
            }}
            animate={{
              y: [0, -50, 0],
              opacity: [0.2, 0.8, 0.2],
              scale: [1, 1.5, 1],
            }}
            transition={{
              duration: shape.duration,
              repeat: Number.POSITIVE_INFINITY,
              delay: shape.delay,
            }}
          />
        ))}
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          ref={headerRef}
          className="text-center mb-20"
          style={{ y: headerY }}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={isHeaderInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.8 }}
          >
            <motion.h2
              className="text-6xl font-black text-[#dfa428] mb-6"
              initial={{ opacity: 0, y: -50 }}
              animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {title}
            </motion.h2>
            <motion.div
              className="text-2xl font-medium text-black mb-4"
              initial={{ opacity: 0, y: -30 }}
              animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {subtitle}
            </motion.div>
            <motion.p
              className="text-lg font-light text-black max-w-2xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: -20 }}
              animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              {description}
            </motion.p>
          </motion.div>

          <Link href="/services">
            <motion.button
              className="yellow-button mt-8 bg-[#dfa428] text-black px-8 py-3 rounded-full font-bold hover:bg-yellow-500 transition-all duration-300"
              initial={{ opacity: 0, y: 30 }}
              animate={isHeaderInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
              whileHover={{ scale: 1.05 }}
            >
              عرض جميع الخدمات
            </motion.button>
          </Link>
        </motion.div>

        {loading ? (
          <div className="py-20 text-center">
            <motion.div
              className="text-2xl font-bold text-[#dfa428]"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 1.5, repeat: Number.POSITIVE_INFINITY }}
            >
              جاري تحميل الخدمات...
            </motion.div>
          </div>
        ) : (
          <div className="space-y-32">
            {services.length > 0 ? (
              services.map((service, index) => (
                <ServiceCard
                  key={service.id || index}
                  service={service}
                  index={index}
                  isReversed={index % 2 === 1}
                  isDesktop={isDesktop}
                />
              ))
            ) : (
              <div className="text-center py-20">
                <p className="text-xl text-black">لا توجد خدمات مميزة حالياً</p>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

const ServiceCard = ({ service, index, isReversed, isDesktop }) => {
  const cardRef = useRef(null);
  const imageRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);

  const { scrollYProgress } = useScroll({
    target: cardRef,
    offset: ["start end", "end start"],
  });

  const imageScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.8, 1, 0.8]);
  const contentOpacity = useTransform(
    scrollYProgress,
    [0, 0.3, 0.7, 1],
    [0, 1, 1, 0]
  );

  const isInView = useInView(cardRef, { once: true, margin: "-100px" });

  // Determine service image safely. Prefer canonicalized media URLs via getMediaUrl.
  const sectionImage =
    service?.sections && service.sections[0]
      ? service.sections[0].image || (Array.isArray(service.sections[0].images) ? service.sections[0].images[0] : undefined)
      : undefined;

  const resolveMedia = (val) => {
    if (!val) return "";
    if (typeof val !== "string") return getMediaUrl(val);
    const trimmed = val.trim();
    if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;
    if (/\.(mp4|webm|mov|avi|mkv|ogg)$/i.test(trimmed)) {
      return getMediaUrl(trimmed);
    }
    const base = servicesData?.mediaBase || "";
    return base ? base + encodeURI(trimmed) : getMediaUrl(trimmed);
  };

  const serviceImage =
    (service && (resolveMedia(service.image) || resolveMedia(sectionImage))) || "/hero.mp4";

  return (
    <motion.div
      ref={cardRef}
      className="relative mb-32"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Background decoration */}
      <motion.div
        className={`absolute ${
          isReversed ? "right-0" : "left-0"
        } top-1/4 w-72 h-72 bg-gradient-to-br from-[#dfa428]/20 to-[#0a367b]/10 rounded-full blur-3xl -z-10`}
        animate={{
          scale: isHovered ? 1.2 : 1,
          opacity: isHovered ? 0.6 : 0.3,
        }}
        transition={{ duration: 0.8 }}
      />

      <div
        className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
          isReversed ? "lg:grid-flow-col-dense" : ""
        }`}
      >
        {/* Image Section */}
        <motion.div
          ref={imageRef}
          className={`relative group ${isReversed ? "lg:col-start-2" : ""}`}
          style={{ scale: imageScale }}
        >
          {/* Main Image Container */}
          <motion.div
            className="relative overflow-hidden rounded-[2rem] shadow-2xl"
            initial={{ opacity: 0, y: 60 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: index * 0.15 }}
          >
            {/* Image/Video */}
              <div className="relative" style={{ paddingTop: '75%' }}>
              {serviceImage.endsWith(".mp4") ? (
                <video
                  src={serviceImage}
                  autoPlay
                  muted
                  loop
                  playsInline
                  className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
              ) : (
                <img
                  src={serviceImage}
                  alt={service && service.title}
                  className="absolute inset-0 object-cover w-full h-full transform group-hover:scale-110 transition-transform duration-700"
                  style={{ objectFit: 'cover' }}
                  loading="eager"
                />
              )}

              {/* Gradient Overlays */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#0a367b]/40 via-transparent to-[#dfa428]/20"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>

              {/* Animated Corner Accent */}
              <motion.div
                className={`absolute ${isReversed ? "left-2" : "right-2"} top-2 w-10 h-10 md:w-20 md:h-20 md:top-8`}
                animate={{ rotate: 360 }}
                transition={{
                  duration: 20,
                  repeat: Number.POSITIVE_INFINITY,
                  ease: "linear",
                }}
              >
                <div className="relative w-full h-full">
                  <div className="absolute inset-0 bg-gradient-to-br from-[#dfa428] to-[#0a367b] rounded-2xl rotate-45 opacity-90"></div>
                  <div className="absolute inset-2 bg-white/10 backdrop-blur-sm rounded-2xl rotate-45"></div>
                </div>
              </motion.div>

              {/* Service Number Badge */}
              <motion.div
                className="absolute bottom-8 left-8 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-6 py-3"
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ delay: index * 0.15 + 0.4 }}
              >
                <span className="text-white text-lg font-bold">
                  {String(index + 1).padStart(2, "0")}
                </span>
              </motion.div>
            </div>
          </motion.div>

          {/* Floating Elements */}
          <motion.div
            className="absolute -top-6 -right-6 w-12 h-12 bg-gradient-to-br from-[#dfa428] to-yellow-600 rounded-full shadow-lg"
            animate={{
              y: [0, -15, 0],
              scale: [1, 1.1, 1],
            }}
            transition={{
              duration: 3,
              repeat: Number.POSITIVE_INFINITY,
              delay: index * 0.3,
            }}
          />
          <motion.div
            className="absolute -bottom-8 -left-8 w-16 h-16 bg-gradient-to-br from-[#0a367b] to-blue-800 rounded-2xl shadow-lg rotate-12"
            animate={{
              rotate: [12, 25, 12],
              scale: [1, 1.05, 1],
            }}
            transition={{
              duration: 4,
              repeat: Number.POSITIVE_INFINITY,
              delay: index * 0.4,
            }}
          />
        </motion.div>

        {/* Content Section */}
        <motion.div
          className={`space-y-8 px-4 lg:px-0 ${
            isReversed ? "lg:col-start-1" : ""
          }`}
          style={{ opacity: contentOpacity }}
        >
          {/* Service Number Large */}
          <motion.div
            className="text-[120px] lg:text-[150px] font-black leading-none"
            initial={{ opacity: 0, scale: 0.5 }}
            animate={isInView ? { opacity: 0.05, scale: 1 } : {}}
            transition={{ duration: 0.8, delay: index * 0.15 }}
          >
            {String(index + 1).padStart(2, "0")}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.15 + 0.2 }}
            className="-mt-20"
          >
            {/* Title with Gradient */}
            <motion.h3
              className="text-3xl lg:text-5xl font-black mb-6 leading-tight bg-gradient-to-r from-[#0a367b] via-[#dfa428] to-[#0a367b] bg-clip-text text-transparent"
              initial={{ opacity: 0, x: isReversed ? -50 : 50 }}
              animate={isInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.15 + 0.3 }}
            >
              {service && service.title}
            </motion.h3>

            {/* Description */}
            <motion.p
              className="text-lg lg:text-xl text-gray-700 leading-relaxed mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.15 + 0.4 }}
            >
              {service && service.description}
            </motion.p>

            {/* Decorative Line */}
            <motion.div
              className="w-24 h-1 bg-gradient-to-r from-[#dfa428] to-transparent rounded-full mb-8"
              initial={{ width: 0 }}
              animate={isInView ? { width: 96 } : {}}
              transition={{ duration: 0.8, delay: index * 0.15 + 0.5 }}
            />

            {/* CTA Button */}
            <Link href={`/ar-IQ/services/${service?.slug || ""}`}>
              <motion.button
                className="group relative bg-gradient-to-r from-[#0a367b] to-[#0a367b]/90 text-white px-10 py-4 rounded-full font-bold text-lg shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden"
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.98 }}
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: index * 0.15 + 0.6 }}
              >
                {/* Button shine effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                  animate={{
                    x: ["-200%", "200%"],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Number.POSITIVE_INFINITY,
                    repeatDelay: 2,
                  }}
                />
                <span className="relative flex items-center gap-3">
                  اعرف المزيد
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{
                      duration: 1.5,
                      repeat: Number.POSITIVE_INFINITY,
                    }}
                  >
                    ←
                  </motion.span>
                </span>
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ServicesSection;
